import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Search, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';

const GlobalSearch = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const canManage = ['admin', 'manager'].includes(user?.role);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [data, setData] = useState({ clients: [], projects: [], tasks: [], invoices: [] });

  useEffect(() => {
    if (!open) return;
    Promise.all([
      canManage ? axios.get('/api/clients') : Promise.resolve({ data: { data: [] } }),
      axios.get('/api/projects'),
      axios.get('/api/tasks'),
      ['admin', 'manager', 'client'].includes(user?.role) ? axios.get('/api/invoices') : Promise.resolve({ data: { data: [] } }),
    ]).then(([clients, projects, tasks, invoices]) => {
      setData({
        clients: clients.data.data || [],
        projects: projects.data.data || [],
        tasks: tasks.data.data || [],
        invoices: invoices.data.data || [],
      });
    }).catch(() => {});
  }, [open, canManage, user?.role]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(true);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return [
      ...data.clients.filter(c => `${c.companyName} ${c.contactPerson} ${c.email}`.toLowerCase().includes(q)).map(c => ({ type: 'Client', title: c.companyName, subtitle: c.email, to: '/clients' })),
      ...data.projects.filter(p => `${p.name} ${p.client?.companyName || ''}`.toLowerCase().includes(q)).map(p => ({ type: 'Project', title: p.name, subtitle: p.client?.companyName, to: `/projects/${p.id}` })),
      ...data.tasks.filter(t => `${t.title} ${t.project?.name || ''}`.toLowerCase().includes(q)).map(t => ({ type: 'Task', title: t.title, subtitle: t.project?.name, to: t.projectId ? `/projects/${t.projectId}` : '/tasks' })),
      ...data.invoices.filter(i => `${i.invoiceNumber} ${i.client?.companyName || ''}`.toLowerCase().includes(q)).map(i => ({ type: 'Invoice', title: i.invoiceNumber, subtitle: i.client?.companyName, to: '/finance' })),
    ].slice(0, 8);
  }, [query, data]);

  const go = (to) => {
    navigate(to);
    setOpen(false);
    setQuery('');
  };

  return (
    <>
      <button className="global-search-trigger" onClick={() => setOpen(true)} title="Search">
        <Search size={15} />
        <span>{t('search')}</span>
      </button>
      {open && (
        <div className="search-overlay" onClick={e => e.target === e.currentTarget && setOpen(false)}>
          <div className="search-modal">
            <div className="search-input-row">
              <Search size={18} />
              <input autoFocus value={query} onChange={e => setQuery(e.target.value)} placeholder="Search clients, projects, tasks, invoices..." />
              <button onClick={() => setOpen(false)} title="Close"><X size={16} /></button>
            </div>
            <div className="search-results">
              {!query.trim() && <div className="search-empty">Start typing to search across the portal.</div>}
              {query.trim() && !results.length && <div className="search-empty">No results found.</div>}
              {results.map((r, i) => (
                <button key={`${r.type}-${r.title}-${i}`} className="search-result" onClick={() => go(r.to)}>
                  <span className="badge badge-gray">{r.type}</span>
                  <span>
                    <strong>{r.title}</strong>
                    {r.subtitle && <small>{r.subtitle}</small>}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default GlobalSearch;
